import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { CancellationToken } from 'vscode/vscode/vs/base/common/cancellation';
import { DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IContextMenuService } from 'vscode/vscode/vs/platform/contextview/browser/contextView.service';
import { IHoverService } from 'vscode/vscode/vs/platform/hover/browser/hover.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { ServiceCollection } from 'vscode/vscode/vs/platform/instantiation/common/serviceCollection';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { IOpenerService } from 'vscode/vscode/vs/platform/opener/common/opener.service';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { ITelemetryService } from 'vscode/vscode/vs/platform/telemetry/common/telemetry.service';
import 'vscode/vscode/vs/platform/theme/common/colorUtils';
import 'vscode/vscode/vs/platform/theme/common/colors/baseColors';
import 'vscode/vscode/vs/platform/theme/common/colors/chartsColors';
import { editorBackground } from 'vscode/vscode/vs/platform/theme/common/colors/editorColors';
import 'vscode/vscode/vs/platform/theme/common/colors/inputColors';
import 'vscode/vscode/vs/platform/theme/common/colors/listColors';
import 'vscode/vscode/vs/platform/theme/common/colors/menuColors';
import 'vscode/vscode/vs/platform/theme/common/colors/minimapColors';
import 'vscode/vscode/vs/platform/theme/common/colors/miscColors';
import 'vscode/vscode/vs/platform/theme/common/colors/quickpickColors';
import 'vscode/vscode/vs/platform/theme/common/colors/searchColors';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import { ViewPane } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPane';
import { Memento } from 'vscode/vscode/vs/workbench/common/memento';
import { SIDE_BAR_FOREGROUND } from 'vscode/vscode/vs/workbench/common/theme';
import { IViewDescriptorService } from 'vscode/vscode/vs/workbench/common/views.service';
import { ChatWidget } from 'vscode/vscode/vs/workbench/contrib/chat/browser/chatWidget';
import { ChatAgentLocation } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents';
import { IChatAgentService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatAgents.service';
import { CHAT_PROVIDER_ID } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatParticipantContribTypes';
import { ChatModelInitState } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatModel';
import { IChatService } from 'vscode/vscode/vs/workbench/contrib/chat/common/chatService.service';

const CHAT_SIDEBAR_PANEL_ID = 'workbench.panel.chatSidebar';
let ChatViewPane = class ChatViewPane extends ViewPane {
    get widget() { return this._widget; }
    constructor(options, keybindingService, contextMenuService, configurationService, contextKeyService, viewDescriptorService, instantiationService, openerService, themeService, telemetryService, hoverService, storageService, chatService, chatAgentService, logService) {
        super(options, keybindingService, contextMenuService, configurationService, contextKeyService, viewDescriptorService, instantiationService, openerService, themeService, telemetryService, hoverService);
        this.storageService = storageService;
        this.chatService = chatService;
        this.chatAgentService = chatAgentService;
        this.logService = logService;
        this.modelDisposables = this._register(( new DisposableStore()));
        this.didProviderRegistrationFail = false;
        this.didUnregisterProvider = false;
        this.memento = ( new Memento('interactive-session-view-' + CHAT_PROVIDER_ID, this.storageService));
        this.viewState = this.memento.getMemento(1 , 1 );
        this._register(this.chatAgentService.onDidChangeAgents(() => {
            if (this.chatAgentService.getDefaultAgent(ChatAgentLocation.Panel)) {
                if (!this._widget?.viewModel) {
                    const sessionId = this.getSessionId();
                    const model = sessionId ? this.chatService.getOrRestoreSession(sessionId) : undefined;
                    try {
                        this._widget.setVisible(false);
                        this.updateModel(model);
                        this.didProviderRegistrationFail = false;
                        this.didUnregisterProvider = false;
                        this._onDidChangeViewWelcomeState.fire();
                    }
                    finally {
                        this.widget.setVisible(true);
                    }
                }
            }
            else if (this._widget?.viewModel?.initState === ChatModelInitState.Initialized) {
                this.didUnregisterProvider = true;
                this._onDidChangeViewWelcomeState.fire();
            }
        }));
    }
    getActionsContext() {
        return {
            chatView: this
        };
    }
    updateModel(model, viewState) {
        this.modelDisposables.clear();
        model = model ?? (this.chatService.transferredSessionData?.sessionId
            ? this.chatService.getOrRestoreSession(this.chatService.transferredSessionData.sessionId)
            : this.chatService.startSession(ChatAgentLocation.Panel, CancellationToken.None));
        if (!model) {
            throw ( new Error('Could not start chat session'));
        }
        this._widget.setModel(model, { ...(viewState ?? this.viewState) });
        this.viewState.sessionId = model.sessionId;
    }
    shouldShowWelcome() {
        const noPersistedSessions = !this.chatService.hasSessions();
        return this.didUnregisterProvider || !this._widget?.viewModel && (noPersistedSessions || this.didProviderRegistrationFail);
    }
    getSessionId() {
        let sessionId;
        if (this.chatService.transferredSessionData) {
            sessionId = this.chatService.transferredSessionData.sessionId;
            this.viewState.inputValue = this.chatService.transferredSessionData.inputValue;
        }
        else {
            sessionId = this.viewState.sessionId;
        }
        return sessionId;
    }
    renderBody(parent) {
        try {
            super.renderBody(parent);
            const scopedInstantiationService = this.instantiationService.createChild(( new ServiceCollection([IContextKeyService, this.scopedContextKeyService])));
            const locationBasedColors = this.getLocationBasedColors();
            this._widget = this._register(scopedInstantiationService.createInstance(ChatWidget, ChatAgentLocation.Panel, { viewId: this.id }, { supportsFileReferences: true }, {
                listForeground: SIDE_BAR_FOREGROUND,
                listBackground: locationBasedColors.background,
                inputEditorBackground: locationBasedColors.background,
                resultEditorBackground: editorBackground
            }));
            this._register(this.onDidChangeBodyVisibility(visible => {
                this._widget.setVisible(visible);
            }));
            this._register(this._widget.onDidClear(() => this.clear()));
            this._widget.render(parent);
            const sessionId = this.getSessionId();
            const disposeListener = sessionId ? this._register(this.chatService.onDidDisposeSession((e) => {
                if (e.reason === 'initializationFailed') {
                    this.didProviderRegistrationFail = true;
                    disposeListener?.dispose();
                    this._onDidChangeViewWelcomeState.fire();
                }
            })) : undefined;
            const model = sessionId ? this.chatService.getOrRestoreSession(sessionId) : undefined;
            this.updateModel(model);
        }
        catch (e) {
            this.logService.error(e);
            throw e;
        }
    }
    acceptInput(query) {
        this._widget.acceptInput(query);
    }
    clear() {
        if (this.widget.viewModel) {
            this.chatService.clearSession(this.widget.viewModel.sessionId);
        }
        this.updateModel(undefined, { ...this.viewState, inputValue: undefined });
    }
    loadSession(sessionId) {
        if (this.widget.viewModel) {
            this.chatService.clearSession(this.widget.viewModel.sessionId);
        }
        const newModel = this.chatService.getOrRestoreSession(sessionId);
        this.updateModel(newModel);
    }
    focusInput() {
        this._widget.focusInput();
    }
    focus() {
        super.focus();
        this._widget.focusInput();
    }
    layoutBody(height, width) {
        super.layoutBody(height, width);
        this._widget.layout(height, width);
    }
    saveState() {
        if (this._widget) {
            this._widget.saveState();
            const widgetViewState = this._widget.getViewState();
            this.viewState.inputValue = widgetViewState.inputValue;
            this.viewState.inputState = widgetViewState.inputState;
            this.memento.saveMemento();
        }
        super.saveState();
    }
};
ChatViewPane = ( __decorate([
    ( __param(1, IKeybindingService)),
    ( __param(2, IContextMenuService)),
    ( __param(3, IConfigurationService)),
    ( __param(4, IContextKeyService)),
    ( __param(5, IViewDescriptorService)),
    ( __param(6, IInstantiationService)),
    ( __param(7, IOpenerService)),
    ( __param(8, IThemeService)),
    ( __param(9, ITelemetryService)),
    ( __param(10, IHoverService)),
    ( __param(11, IStorageService)),
    ( __param(12, IChatService)),
    ( __param(13, IChatAgentService)),
    ( __param(14, ILogService))
], ChatViewPane));

export { CHAT_SIDEBAR_PANEL_ID, ChatViewPane };
